import useFormatCurrencyForSrp from '@/src/hooks/currency/useFormatCurrencyForSrp'
import Box from '@mui/material/Box'
import Slider from '@mui/material/Slider'
import { styled } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useRef, useState } from 'react'

const PriceRangeSlider = styled(Slider)(({ theme }) => ({
    color: theme.palette.primary.main,
    height: 8,
    padding: '15px 0',
    '& .MuiSlider-thumb': {
        height: 20,
        width: 20,
        backgroundColor: theme.palette.primary.main,
        border: '2px solid #fff',
        boxShadow: '0 0 4px 0px rgba(0, 0, 0, 0.2)',
        '&:focus, &:hover, &.Mui-active': {
            boxShadow: '0 0 6px 0px rgba(0, 0, 0, 0.3)'
        },
        '&:before': {
            display: 'none'
        }
    },
    '& .MuiSlider-valueLabel': {
        display: 'none'
    },
    '& .MuiSlider-track': {
        border: 'none',
        height: 8,
        backgroundColor: theme.palette.primary.main
    },
    '& .MuiSlider-rail': {
        opacity: 1,
        backgroundColor: '#d0d0d0',
        height: 8
    }
}))

interface PriceRangeFilterProps {
    minPrice: number
    maxPrice: number
    currentMin: number
    currentMax: number
    onPriceChange?: (min: number, max: number) => void
    title: string
}

const PriceRangeFilter: React.FC<PriceRangeFilterProps> = ({
    minPrice,
    maxPrice,
    currentMin,
    currentMax,
    onPriceChange,
    title
}) => {
    const [value, setValue] = useState<number[]>([currentMin, currentMax])
    const { formatCurrency } = useFormatCurrencyForSrp()
    const timerRef = useRef<NodeJS.Timeout | null>(null)

    const handleChange = useCallback(
        (_: Event, newValue: number[] | number) => {
            if (Array.isArray(newValue)) {
                setValue(newValue)

                //update to the parent with debounce
                if (timerRef.current) {
                    clearTimeout(timerRef.current)
                }
                timerRef.current = setTimeout(() => {
                    onPriceChange?.(newValue[0], newValue[1])
                }, 500)
            }
        },
        [onPriceChange]
    )

    const formatPrice = (price: number) => {
        return formatCurrency(price)
    }

    //sync if parent store changes from other component
    useEffect(() => {
        setValue(prev => {
            if (prev[0] !== currentMin || prev[1] !== currentMax) {
                return [currentMin, currentMax]
            }
            return prev
        })
    }, [currentMin, currentMax])

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                    {title}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '0.75rem' }}>
                    {formatPrice(value[0])} - {formatPrice(value[1])}
                </Typography>
            </Box>
            <Box px={1}>
                <PriceRangeSlider
                    value={value}
                    onChange={handleChange}
                    valueLabelDisplay="off"
                    min={minPrice}
                    max={maxPrice}
                    getAriaLabel={index => (index === 0 ? 'Minimum price' : 'Maximum price')}
                    disableSwap
                />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {formatPrice(minPrice)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                    {formatPrice(maxPrice)}
                </Typography>
            </Box>
        </Box>
    )
}

export default PriceRangeFilter
